import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueService, SeasonService } from '../../services/firebaseService';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  UserGroupIcon,
  CogIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const schema = yup.object({
  name: yup.string().required('League name is required').min(3, 'League name must be at least 3 characters'),
  description: yup.string().optional(),
  maxTeams: yup.number().required('Maximum teams is required').min(2, 'Minimum 2 teams').max(100, 'Maximum 100 teams'),
  isPrivate: yup.boolean().required(),
}).required();

const defaultScoringRules = {
  survivalPoints: 1,
  winnerPoints: 1,
  postMergePredictionPoints: 3,
  preMergePredictionPoints: 3
};

export default function CreateLeague() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeSeasons, setActiveSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      maxTeams: 8,
      isPrivate: true,
    }
  });

  const isPrivate = watch('isPrivate');

  useEffect(() => {
    const fetchActiveSeasons = async () => {
      try {
        const seasons = await SeasonService.getActiveSeasons();
        setActiveSeasons(seasons);
        if (seasons.length > 0) {
          setSelectedSeason(seasons[0].id);
        }
      } catch (error) {
        console.error('Error fetching active seasons:', error);
        setError('Failed to load active seasons');
      }
    };

    fetchActiveSeasons();
  }, []);

  const onSubmit = async (data) => {
    if (!selectedSeason) {
      setError('Please select a season');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const leagueData = {
        name: data.name,
        description: data.description || '',
        managerId: currentUser.uid,
        maxTeams: data.maxTeams,
        isPrivate: data.isPrivate,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Create the league
      const leagueId = await LeagueService.createLeague(leagueData);

      // Create the initial season document for this league
      const seasonData = {
        seasonId: selectedSeason,
        isActive: true,
        draftStatus: 'notStarted',
        scoringRules: defaultScoringRules,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await LeagueService.createLeagueSeason(leagueId, seasonData);

      // Update the user's managedLeagues array
      await LeagueService.addManagerToLeague(leagueId, currentUser.uid);

      navigate(`/manage-leagues/${leagueId}`, { 
        state: { 
          message: 'League created successfully!'
        } 
      });
    } catch (error) {
      console.error('Error creating league:', error);
      setError('Failed to create league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/manage-leagues')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Create New League</h1>
          </div>
          <p className="text-gray-600">
            Set up a new Survivor fantasy league and invite players to compete.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* League Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <TrophyIcon className="w-6 h-6 text-survivor-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">League Information</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  League Name *
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter league name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  {...register('description')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="Optional description of your league"
                />
              </div>

              <div>
                <label htmlFor="season" className="block text-sm font-medium text-gray-700 mb-1">
                  Season *
                </label>
                <select
                  id="season"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                >
                  <option value="">Select a season</option>
                  {activeSeasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
                {activeSeasons.length === 0 && (
                  <p className="mt-1 text-sm text-yellow-600">
                    No active seasons available. Please contact an admin to create a season.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* League Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <CogIcon className="w-6 h-6 text-survivor-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">League Settings</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="maxTeams" className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Teams *
                </label>
                <input
                  type="number"
                  id="maxTeams"
                  {...register('maxTeams')}
                  min="2"
                  max="100"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                    errors.maxTeams ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.maxTeams && (
                  <p className="mt-1 text-sm text-red-600">{errors.maxTeams.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPrivate"
                  {...register('isPrivate')}
                  className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
                />
                <label htmlFor="isPrivate" className="ml-2 block text-sm text-gray-900">
                  Private League
                </label>
              </div>
              <p className="text-sm text-gray-500">
                Private leagues require an invitation code to join. Public leagues can be found and joined by anyone.
              </p>
            </div>
          </div>

          {/* Scoring Rules Preview */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-survivor-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Default Scoring Rules</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span>Survival Points:</span>
                <span className="font-medium">{defaultScoringRules.survivalPoints} pts</span>
              </div>
              <div className="flex justify-between">
                <span>Winner Points:</span>
                <span className="font-medium">{defaultScoringRules.winnerPoints} pts</span>
              </div>
              <div className="flex justify-between">
                <span>Pre-merge Predictions:</span>
                <span className="font-medium">{defaultScoringRules.preMergePredictionPoints} pts</span>
              </div>
              <div className="flex justify-between">
                <span>Post-merge Predictions:</span>
                <span className="font-medium">{defaultScoringRules.postMergePredictionPoints} pts</span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              These are the default scoring rules. You can customize them after creating the league.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/manage-leagues')}
              className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading || !selectedSeason}
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Create League
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
